import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCrewLinkNotifications } from '../context/CrewLinkNotificationContext';
import { useExchangeNotifications } from '../context/ExchangeNotificationContext';
import DMInbox from './messaging/DMInbox';
import './UserArea.css';

// ── Notification helpers ──

const formatNotificationTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now - time;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return time.toLocaleDateString();
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'comment_reply': return '\uD83D\uDCAC';
    case 'comment_like': return '\u2764\uFE0F';
    case 'mention': return '@';
    case 'system': return '\u2139\uFE0F';
    case 'listing_interest': return '\uD83D\uDCB0';
    case 'interest_accepted': return '\u2705';
    case 'interest_rejected': return '\u274C';
    case 'interest_status_update': return '\uD83D\uDCCB';
    case 'transaction_confirmation': return '\u23F3';
    case 'transaction_complete': return '\uD83C\uDF89';
    case 'transaction_cancelled': return '\uD83D\uDEAB';
    case 'org_invite': return '\uD83C\uDFE2';
    case 'org_gift': return '\uD83C\uDF81';
    case 'org_gift_accepted': return '\u2728';
    case 'fleet_share_request': return '\uD83D\uDE80';
    case 'fleet_share_granted': return '\u2705';
    default: return '\uD83D\uDD14';
  }
};

const getNotificationMessage = (notification) => {
  const { type, context, message } = notification;
  switch (type) {
    case 'comment_reply':
      return `${context.replier_username || 'Someone'} replied to your comment`;
    case 'comment_like':
      return `${context.liker_username || 'Someone'} liked your comment`;
    case 'mention':
      return `${context.mentioner_username || 'Someone'} mentioned you`;
    case 'system':
      return context.message || 'System notification';
    case 'org_invite':
      return message || `You've been invited to join ${context?.org_name || 'an organization'}`;
    case 'org_gift':
      return message || 'Someone gifted subscription time to your org';
    case 'org_gift_accepted':
      return message || 'Your gift was accepted';
    case 'fleet_share_request':
      return message || 'Your org leader requested access to view your fleet';
    case 'fleet_share_granted':
      return message || 'Fleet share approved';
    case 'listing_interest':
    case 'interest_accepted':
    case 'interest_rejected':
    case 'interest_status_update':
    case 'transaction_confirmation':
    case 'transaction_complete':
    case 'transaction_cancelled':
      return message || 'Marketplace notification';
    default:
      return message || 'New notification';
  }
};

// ── UserArea Component ──

const UserArea = ({ mobile = false, onNavigate, openDmConversations, setOpenDmConversations }) => {
  const {
    currentUser,
    logout,
    isAuthenticated,
    hasPremiumAccess,
    hasAnyOrgMembership,
    orgMemberships,
    supabase,
  } = useAuth();
  // CrewLink and Exchange notification state — consumed by context but
  // badges are now displayed inside MegaMenu panels, not in UserArea.
  useCrewLinkNotifications();
  useExchangeNotifications();
  const navigate = useNavigate();

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [orgList, setOrgList] = useState([]);

  const profileHideRef = useRef(null);
  const orgHideRef = useRef(null);
  const notifHideRef = useRef(null);

  // Expose global setter for unread count
  useEffect(() => {
    window.__setNavUnreadCount = (count) => setUnreadCount(Number(count) || 0);
    return () => { if (window.__setNavUnreadCount) delete window.__setNavUnreadCount; };
  }, []);

  // Load org names
  useEffect(() => {
    (async () => {
      try {
        if (!isAuthenticated || !Array.isArray(orgMemberships) || orgMemberships.length === 0) {
          setOrgList([]);
          return;
        }
        const ids = Array.from(new Set(orgMemberships.map(m => m.org_id).filter(Boolean)));
        if (ids.length === 0) { setOrgList([]); return; }
        const { data, error } = await supabase.from('orgs').select('id,name,slug').in('id', ids);
        if (!error && Array.isArray(data)) {
          setOrgList(data);
        } else {
          setOrgList(ids.map(id => ({ id, name: 'Organization' })));
        }
      } catch { setOrgList([]); }
    })();
  }, [isAuthenticated, orgMemberships, supabase]);

  // Fetch notifications with Realtime
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated || !currentUser) { setNotifications([]); return; }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/auth/notifications`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) return;
        if (response.ok) {
          const result = await response.json();
          setNotifications((result.notifications || []).slice(0, 5));
        }
      } catch (err) {
        console.error('[UserArea] Error fetching notifications:', err);
      }
    };

    fetchNotifications();

    let channel = null;
    if (isAuthenticated && currentUser?.id) {
      channel = supabase
        .channel(`notifications:${currentUser.id}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${currentUser.id}` }, (payload) => {
          setNotifications((prev) => [payload.new, ...prev].slice(0, 5));
          if (!payload.new.is_read) setUnreadCount((prev) => prev + 1);
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${currentUser.id}` }, (payload) => {
          setNotifications((prev) => prev.map((n) => (n.id === payload.new.id ? payload.new : n)));
        })
        .subscribe();
    }

    const interval = setInterval(fetchNotifications, 300000);
    return () => {
      clearInterval(interval);
      if (channel) supabase.removeChannel(channel);
    };
  }, [isAuthenticated, currentUser?.id, supabase]);

  // ── Hover handlers ──
  const makeHoverHandlers = (setShow, hideRef) => ({
    onEnter: () => {
      if (hideRef.current) { clearTimeout(hideRef.current); hideRef.current = null; }
      setShow(true);
    },
    onLeave: () => {
      if (hideRef.current) clearTimeout(hideRef.current);
      hideRef.current = setTimeout(() => { setShow(false); hideRef.current = null; }, 300);
    },
  });

  const profileHover = makeHoverHandlers(setShowProfileDropdown, profileHideRef);
  const orgHover = makeHoverHandlers(setShowOrgDropdown, orgHideRef);
  const notifHover = makeHoverHandlers(setShowNotificationDropdown, notifHideRef);

  useEffect(() => {
    return () => {
      [profileHideRef, orgHideRef, notifHideRef].forEach(ref => {
        if (ref.current) clearTimeout(ref.current);
      });
    };
  }, []);

  // ── Actions ──
  const handleLogout = async () => {
    await logout();
    navigate('/login');
    if (onNavigate) onNavigate();
  };

  const handleNotificationClick = async (notification) => {
    setShowNotificationDropdown(false);
    if (onNavigate) onNavigate();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        const API_URL = process.env.REACT_APP_API_URL || '';
        await fetch(`${API_URL}/api/auth/notifications/${notification.id}/read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) { console.error('Error marking notification as read:', err); }
    if (notification.type === 'comment_reply' && notification.context?.item_id && notification.context?.comment_id) {
      navigate(`/items?itemId=${notification.context.item_id}&commentId=${notification.context.comment_id}`);
    } else if (notification.context?.link_url) {
      // Security: only allow relative paths to prevent open redirect
      const linkUrl = notification.context.link_url;
      if (linkUrl && linkUrl.startsWith('/') && !linkUrl.startsWith('//')) {
        navigate(linkUrl);
      }
    }
  };

  const handleDeleteNotification = async (e, notification) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      await fetch(`${API_URL}/api/auth/notifications/${notification.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
      });
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      if (!notification.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error('[UserArea] Error deleting notification:', err); }
  };

  const handleClearNotifications = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      await fetch(`${API_URL}/api/auth/notifications/clear`, {
        method: 'POST',
        headers: { 'Authorization': token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) { console.error('[UserArea] Error clearing notifications:', err); }
  };

  // ── Display name ──
  let profileDisplay = 'Profile';
  if (currentUser) {
    if (currentUser.username) profileDisplay = currentUser.username;
    else if (currentUser.email) profileDisplay = currentUser.email.split('@')[0];
  }

  const hasOrg = (orgList && orgList.length > 0) || hasAnyOrgMembership;

  const wrapperClass = mobile ? 'user-area user-area--mobile' : 'user-area';

  // ── Unauthenticated ──
  if (!isAuthenticated) {
    return (
      <div className={wrapperClass}>
        <Link to="/login" className="user-area__auth-btn user-area__auth-btn--signin" onClick={onNavigate}>
          Sign In
        </Link>
        <Link to="/register" className="user-area__auth-btn user-area__auth-btn--signup" onClick={onNavigate}>
          Sign Up
        </Link>
      </div>
    );
  }

  // ── Authenticated ──
  return (
    <div className={wrapperClass}>
      {/* Profile dropdown */}
      <div
        className="user-area__profile-wrapper"
        onMouseEnter={profileHover.onEnter}
        onMouseLeave={profileHover.onLeave}
      >
        <button
          className="user-area__profile-btn"
          aria-haspopup="true"
          aria-expanded={showProfileDropdown}
          onClick={() => setShowProfileDropdown(prev => !prev)}
        >
          {profileDisplay}
          <span className="user-area__profile-arrow">&#9660;</span>
          {unreadCount > 0 && (
            <span className="user-area__notification-badge" style={{ position: 'static', marginLeft: 4 }}>
              {unreadCount}
            </span>
          )}
        </button>
        {showProfileDropdown && (
          <ul
            className="user-area__profile-dropdown"
            onMouseEnter={profileHover.onEnter}
            onMouseLeave={profileHover.onLeave}
          >
            <li>
              <Link to="/profile" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                Settings
              </Link>
            </li>
            <li>
              <Link to="/profile/security" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                Security
              </Link>
            </li>
            <li>
              <Link to="/my-ledger" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                My Ledger
              </Link>
            </li>
            {['moderator', 'admin', 'owner'].includes(currentUser?.role) && (
              <>
                <div className="user-area__dropdown-divider" />
                <li className="user-area__dropdown-link--role">
                  {currentUser.role}
                </li>
                <li>
                  <Link to="/moderator/review-queue" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                    Moderator Panel
                  </Link>
                </li>
                <li>
                  <Link to="/moderator/loot-reports" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                    Loot Reports
                  </Link>
                </li>
              </>
            )}
            {(currentUser?.role === 'admin' || currentUser?.role === 'owner') && (
              <li>
                <Link to="/admin/v2" className="user-area__dropdown-link" onClick={() => { setShowProfileDropdown(false); if (onNavigate) onNavigate(); }}>
                  Admin Dashboard
                </Link>
              </li>
            )}
            <div className="user-area__dropdown-divider" />
            <li>
              <button
                className="user-area__dropdown-link user-area__dropdown-link--logout"
                onClick={handleLogout}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 'inherit' }}
              >
                Logout
              </button>
            </li>
          </ul>
        )}
      </div>

      {/* Ledger link */}
      <Link to="/my-ledger" className="user-area__action-link" onClick={onNavigate}>
        Ledger
      </Link>

      {/* Submit Data */}
      <Link to="/submit-data" className="user-area__action-link user-area__action-link--submit" onClick={onNavigate}>
        Submit Data
      </Link>

      {/* Org dropdown */}
      {hasOrg && (
        <div
          className="user-area__org-wrapper"
          onMouseEnter={orgHover.onEnter}
          onMouseLeave={orgHover.onLeave}
        >
          <button
            className="user-area__org-btn"
            aria-haspopup="true"
            aria-expanded={showOrgDropdown}
            onClick={() => setShowOrgDropdown(prev => !prev)}
          >
            Org <span className="user-area__profile-arrow">&#9660;</span>
          </button>
          {showOrgDropdown && (
            <ul
              className="user-area__org-dropdown"
              onMouseEnter={orgHover.onEnter}
              onMouseLeave={orgHover.onLeave}
            >
              {orgList.map((org) => (
                <li key={org.id}>
                  <Link
                    to={`/org/${org.slug || org.id}`}
                    className="user-area__dropdown-link"
                    style={{ fontWeight: 700 }}
                    onClick={() => { setShowOrgDropdown(false); if (onNavigate) onNavigate(); }}
                  >
                    {org.name || 'Organization'}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Notification bell */}
      <div
        className="user-area__notification-wrapper"
        onMouseEnter={notifHover.onEnter}
        onMouseLeave={notifHover.onLeave}
      >
        <button
          className="user-area__notification-btn"
          aria-haspopup="true"
          aria-expanded={showNotificationDropdown}
          onClick={() => setShowNotificationDropdown(prev => !prev)}
        >
          {'\uD83D\uDD14'}
          {unreadCount > 0 && (
            <span className="user-area__notification-badge">{unreadCount}</span>
          )}
        </button>
        {showNotificationDropdown && (
          <ul
            className="user-area__notification-dropdown"
            onMouseEnter={notifHover.onEnter}
            onMouseLeave={notifHover.onLeave}
          >
            {notifications.length === 0 ? (
              <li className="user-area__notification-empty">No recent notifications</li>
            ) : (
              <>
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={`user-area__notification-item ${!notification.is_read ? 'user-area__notification-item--unread' : ''}`}
                  >
                    <button
                      className="user-area__notification-item-btn"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <span className="user-area__notification-icon">
                        {getNotificationIcon(notification.type)}
                      </span>
                      <div className="user-area__notification-text">
                        <div className="user-area__notification-message">
                          {getNotificationMessage(notification)}
                        </div>
                        <div className="user-area__notification-time">
                          {formatNotificationTime(notification.created_at)}
                        </div>
                      </div>
                    </button>
                    <button
                      className="user-area__notification-delete"
                      onClick={(e) => handleDeleteNotification(e, notification)}
                      title="Delete notification"
                    >
                      &#10005;
                    </button>
                  </li>
                ))}
                <li className="user-area__notification-footer">
                  <Link
                    to="/notifications"
                    onClick={() => { setShowNotificationDropdown(false); if (onNavigate) onNavigate(); }}
                  >
                    View All
                  </Link>
                  <button
                    className="user-area__notification-clear-btn"
                    onClick={handleClearNotifications}
                  >
                    Clear All
                  </button>
                </li>
              </>
            )}
          </ul>
        )}
      </div>

      {/* DM Inbox */}
      {currentUser?.dm_enabled !== false && (
        <div className="user-area__dm-wrapper">
          <DMInbox onOpenConversation={(partner) => {
            if (!setOpenDmConversations) return;
            const existing = openDmConversations?.find(conv => conv.recipientId === partner.id);
            if (existing) return;
            setOpenDmConversations(prev => [...prev, {
              recipientId: partner.id,
              recipientName: partner.display_name || partner.username,
            }]);
          }} />
        </div>
      )}

      {/* Upgrade link */}
      {!hasPremiumAccess && (
        <Link to="/purchase" className="user-area__action-link user-area__action-link--upgrade" onClick={onNavigate}>
          Upgrade
        </Link>
      )}
    </div>
  );
};

export default UserArea;
